// Falling-item spawner. Tuning lives in BE_side/app/character_layout.json
// under gamePlay.spawnTime (min/max). HAZARD_PROBABILITY and the no-overlap
// retry policy stay in code (not in the BE config).

import { HAZARD_PROBABILITY, SPAWN_NO_OVERLAP } from "../config/gameplay";
import { SCENE_WIDTH } from "../config/scene";
import type { FallingItem } from "./physics";

export type SpawnerState = {
  countdown: number;
  nextId: number;
};

export type SpawnTime = { min: number; max: number };

/** Random delay in [min, max]. If max < min the runtime collapses to min
 *  (per the spec). */
export function nextSpawnDelay(spawn: SpawnTime, rand: () => number = Math.random): number {
  const effMax = spawn.max < spawn.min ? spawn.min : spawn.max;
  return spawn.min + rand() * (effMax - spawn.min);
}

export function createSpawner(spawn: SpawnTime, rand: () => number = Math.random): SpawnerState {
  return { countdown: nextSpawnDelay(spawn, rand), nextId: 1 };
}

export type SpawnSources = {
  ingredientCount: number;
  hazardCount: number;
};

export function tickSpawner(
  state: SpawnerState,
  dt: number,
  liveItems: readonly FallingItem[],
  sources: SpawnSources,
  now: number,
  spawn: SpawnTime,
  itemVisualSize: number,
  rand: () => number = Math.random,
): FallingItem | null {
  state.countdown -= dt;
  if (state.countdown > 0) return null;

  // Pick X with optional retry to avoid overlap in the top band.
  const half = itemVisualSize / 2;
  let x = 0;
  const maxAttempts = SPAWN_NO_OVERLAP ? 5 : 1;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    x = half + rand() * (SCENE_WIDTH - itemVisualSize);
    if (!SPAWN_NO_OVERLAP) break;
    const conflict = liveItems.some(
      (it) => now - it.spawnTime < 0.25 && Math.abs(it.x - x) < itemVisualSize,
    );
    if (!conflict) break;
  }

  const isHazard = rand() < HAZARD_PROBABILITY;
  const item: FallingItem = {
    id: state.nextId++,
    kind: isHazard ? "hazard" : "ingredient",
    spriteIndex: Math.floor(rand() * (isHazard ? sources.hazardCount : sources.ingredientCount)),
    x,
    spawnTime: now,
    rotation: rand() * Math.PI * 2,
  };

  state.countdown = nextSpawnDelay(spawn, rand);
  return item;
}
